import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { CreateFAQDialogComponent } from '../create-faq-dialog/create-faq-dialog.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-view-faq',
  templateUrl: './view-faq.component.html',
  styleUrls: ['./view-faq.component.scss']
})
export class ViewFAQComponent implements OnInit {
  AppConstants = AppConstants;
  faqId: string = '';
  faqDetails: any = null;
  showConfirmDialog: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this._route.params.subscribe(params => {
      this.faqId = params['id'];
      if (this.faqId) {
        this.fetchFAQDetails();
      }
    });
  }

  async fetchFAQDetails(): Promise<void> {
    this._loaderService.showLoader();
    
    try {
      // Extract numeric ID from faqId (supports both "15" and "FAQ-15" formats)
      let faqIdToSend = this.faqId;
      
      // If it's a number, use it directly
      const numericId = parseInt(this.faqId, 10);
      if (!isNaN(numericId)) {
        faqIdToSend = numericId.toString();
      } else {
        // Try to extract number from string like "FAQ-15"
        const match = this.faqId.match(/\d+/);
        if (match) {
          faqIdToSend = match[0];
        } else {
          // If no number found, send the original string (might be FAQId format)
          faqIdToSend = this.faqId;
        }
      }

      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.FAQs}/${AppConstants.ACTIONS.GetFAQById}/${faqIdToSend}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response && (response.IsSuccess || response.isSuccess) && response.data) {
        const faq = response.data;
        
        // Extract numeric ID for fallback
        const numericId = parseInt(this.faqId, 10) || (this.faqId.match(/\d+/) ? parseInt(this.faqId.match(/\d+/)?.[0] || '0', 10) : 0);
        
        // Map API response to component format
        this.faqDetails = {
          id: faq.id || faq.ID || numericId,
          faqId: faq.faqId || faq.FAQId || '',
          linkedTenderId: faq.linkedTenderId || faq.LinkedTenderId || '',
          linkedTender: faq.linkedTenderId ? {
            tenderId: faq.linkedTenderId || faq.LinkedTenderId || '',
            title: faq.linkedTenderTitle || faq.tender?.tenderName || faq.tender?.TenderName || ''
          } : null,
          question: faq.question || faq.Question || '',
          answer: faq.answer || faq.Answer || '',
          status: faq.status || faq.Status || 'Draft',
          createdBy: faq.createdBy || faq.CreatedBy || '',
          createdDate: faq.createdDate || faq.CreatedDate ? new Date(faq.createdDate || faq.CreatedDate) : null,
          lastModifiedBy: faq.modifiedBy || faq.ModifiedBy || '',
          lastModifiedDate: faq.modifiedDate || faq.ModifiedDate ? new Date(faq.modifiedDate || faq.ModifiedDate) : null
        };
        
        // Trigger change detection to update the view
        this.cdr.detectChanges();
      } else {
        const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || response?.message || 'FAQ not found';
        this.toasterService.showError(errorMessage, 'Error');
        this._router.navigate([`/${AppConstants.ROUTES.FAQS}`]);
      }
    } catch (error: any) {
      console.error('Failed to fetch FAQ details:', error);
      const errorMessage = error?.error?.ErrorMessageEn || 
                          error?.error?.errorMessageEn || 
                          error?.error?.message || 
                          error?.message || 
                          'An error occurred while fetching FAQ details';
      this.toasterService.showError(errorMessage, 'Error');
      this._router.navigate([`/${AppConstants.ROUTES.FAQS}`]);
    } finally {
      this._loaderService.hideLoader();
    }
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.FAQS}`]);
  }

  async publishFAQ(): Promise<void> {
    if (!this.faqDetails) {
      this.toasterService.showError('FAQ details not loaded', 'Error');
      return;
    }

    // Get numeric ID for publishing
    const faqIdToPublish = this.faqDetails.id || this.faqDetails.ID;
    if (!faqIdToPublish) {
      this.toasterService.showError('FAQ ID is missing', 'Error');
      return;
    }

    this._loaderService.showLoader();
    
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.FAQs}/${AppConstants.ACTIONS.PublishFAQ}/${faqIdToPublish}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));
      
      if (response && (response.IsSuccess || response.isSuccess)) {
        this.toasterService.showSuccess('FAQ published successfully', 'Success');
        // Refresh FAQ details to get updated status
        await this.fetchFAQDetails();
      } else {
        const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || response?.message || 'Failed to publish FAQ';
        this.toasterService.showError(errorMessage, 'Error');
      }
    } catch (error: any) {
      const errorMessage = error?.error?.ErrorMessageEn || 
                          error?.error?.errorMessageEn || 
                          error?.error?.message || 
                          error?.message || 
                          'An error occurred while publishing the FAQ';
      this.toasterService.showError(errorMessage, 'Error');
      console.error('[View FAQ] Error publishing FAQ:', error);
    } finally {
      this._loaderService.hideLoader();
    }
  }

  editFAQ(): void {
    if (!this.faqDetails) {
      this.toasterService.showError('FAQ details not loaded', 'Error');
      return;
    }

    const dialogRef = this.dialog.open(CreateFAQDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'create-faq-dialog',
      data: { faq: this.faqDetails } // Pass FAQ data for editing
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh FAQ details after successful update
        this.fetchFAQDetails();
      }
    });
  }

  deleteFAQ(): void {
    if (!this.faqDetails) {
      this.toasterService.showError('FAQ details not loaded', 'Error');
      return;
    }

    this.showConfirmDialog = true;
  }

  confirmDeleteFAQ(): void {
    if (!this.faqDetails) {
      this.showConfirmDialog = false;
      return;
    }

    this.showConfirmDialog = false;
    this._loaderService.showLoader();
    
    // Get numeric ID for deletion
    const faqIdToDelete = this.faqDetails.id || this.faqDetails.ID;
    if (!faqIdToDelete) {
      this._loaderService.hideLoader();
      this.toasterService.showError('FAQ ID is missing', 'Error');
      return;
    }

    const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.FAQs}/${AppConstants.ACTIONS.DeleteFAQ}/${faqIdToDelete}`;
    
    this.apiService.deleteByUrl(url, {}, {}).subscribe({
      next: (response: any) => {
        this._loaderService.hideLoader();
        if (response && (response.IsSuccess || response.isSuccess || response.data)) {
          this.toasterService.showSuccess('FAQ deleted successfully', 'Success');
          // Navigate back to FAQs list
          this._router.navigate([`/${AppConstants.ROUTES.FAQS}`]);
        } else {
          const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || response?.message || 'Failed to delete FAQ';
          this.toasterService.showError(errorMessage, 'Error');
        }
      },
      error: (error: any) => {
        this._loaderService.hideLoader();
        const errorMessage = error?.error?.ErrorMessageEn || 
                            error?.error?.errorMessageEn || 
                            error?.error?.message || 
                            error?.message || 
                            'An error occurred while deleting the FAQ';
        this.toasterService.showError(errorMessage, 'Error');
        console.error('[View FAQ] Error deleting FAQ:', error);
      }
    });
  }

  cancelDeleteFAQ(): void {
    this.showConfirmDialog = false;
  }

  getConfirmDialogMessage(): string {
    const faqIdDisplay = this.faqDetails?.faqId || this.faqDetails?.id || this.faqId;
    return `Are you sure you want to delete FAQ "${faqIdDisplay}"?`;
  }

  getStatusColor(status: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    switch(status?.toLowerCase()) {
      case 'published':
        return 'green';
      case 'draft':
        return 'gray';
      case 'archived':
        return 'yellow';
      default:
        return 'gray';
    }
  }
}

